<template>
  <div :class="$style['login-container']">
      <div :class="$style['login-box']">
        <div :class="$style['login-box-logo']">
          <img src="@/assets/images/logo.png" :class="$style['login-box-logo-img']" alt="logo">
        </div>
        <form submit="#">
          <div style="display: flex;flex-direction: column;align-items: center;">
            <div :class="$style['input-group']">
              <label for="user">User</label>
              <input v-model="user" type="text" name="user" id="user" :class="[$style['input'], {[$style['error']]: errorUser}]" placeholder="User">
              <span v-if="errorUser" :class="$style['error-message']">
                Invalid Field
              </span>
            </div>
            <div :class="$style['input-group']">
              <label for="password">Password</label>
              <div :class="$style['input-wrapper']">
                <input v-model="password" :type="passwordType()" name="password" id="password" :class="[$style['input'], {[$style['error']]: errorPassword}]" :placeholder="passwordPlaceholder()">
                <button type="button" @click="togglePasswordVisibility" :class="$style['toggle-password']">
                  <EyeIcon v-if="isPasswordVisible" />
                  <EyeSlashIcon v-else />
                </button>
              </div>
              <span v-if="errorPassword" :class="$style['error-message']">
                Invalid Field
              </span>
            </div>
            <span :class="$style['error-message']" v-if="errorLogin">
              Please check your credentials or try again later.
            </span>
          </div>
          <div :class="$style['login-box-button']">
            <button v-if="!loading" type="submit" :class="[$style['button'], $style['outline']]" @click.prevent="doLogin()">Login</button>
            <LoadingSpinner v-else/>
          </div>
        </Form>
      </div>
  </div>
</template>

<script>
import { EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import authService from '@/services/authService';
import { viewOnlyUsersBalanceTableId, viewOnlyUsersBalanceTableIdxName } from '@/enums/users';

export default {
  name: 'LoginPage',
  data() {
    return {
      isPasswordVisible: false,
      user: null,
      password: null,
      errorUser: false,
      errorPassword: false,
      errorLogin: false,
      loading: false,
    }
  },
  mounted() {
    localStorage.removeItem('token')
  },
  components: {
    EyeIcon,
    EyeSlashIcon,
    LoadingSpinner
  },

  methods: {
    passwordType() {
      return this.isPasswordVisible ? 'text' : 'password'
    },
    passwordPlaceholder() {
      return this.isPasswordVisible ? 'Password' : '********'
    },
    togglePasswordVisibility() {
      this.isPasswordVisible = !this.isPasswordVisible
    },
    validateUser() {
      if ((this.user == null || this.user?.trim().length < 1)) {
        this.errorUser = true
        this.loading = false
      } else {
        this.errorUser = false
      }
    },
    validatePassword() {
      if (this.password == null || this.password?.trim().length < 1) {
        this.errorPassword = true
        this.loading = false
      } else {
        this.errorPassword = false
      }
    },
    validateFields() {
      this.validateUser()
      this.validatePassword()
    },
    async doLogin() {
      this.errorLogin = false
      this.loading = true

      this.validateFields()
      
      if(this.errorUser || this.errorPassword) return

      try {
        const response = await authService.login(this.user, this.password);

        const id = response.data.user_id
        const token = response.data.access
        const master = response.data.is_master
        const user = this.user

        this.$store.dispatch('auth/login', { token, user, master, id })

        if (viewOnlyUsersBalanceTableId.includes(id) && viewOnlyUsersBalanceTableIdxName[id] == user) {
          this.$router.push({name: 'UsersBalance'});
        } else {
          this.$router.push({name: 'Home'});
        }

      } catch (error) {
        this.errorLogin = true
        this.loading = false
      }
    }
  }
}
</script>
<style lang="scss" module>

  @include respond-to(null,'xs') {
    .input-group {
      input {
        width: 220px !important;
      }
    }

    .login-container {
      .login-box {
        padding: 30px 0 0 0 !important;
        flex: 1;
        height: 100%;
        border-radius: unset !important;
        
        &-button {
          padding-top: unset !important;
        }

        &-logo{
          &-img {
            width: 250px;
          }
        }
      }
    }

  }

  @include respond-to('xs', 'sm') {
    .login-container {
      .login-box {
        padding: 30px 0 0 0 !important;
        flex: 1;
        height: 100%;
      }
    }

    .input-group {
      margin-bottom: 20px;
    }

    .button {
      margin-top: 15px;
    }
  }

  .login-container {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;

    .login-box {
      padding: 90px 125px;
      border-radius: 16px;
      background: rgba(0, 0, 0, 0.35);
  
      &-logo {
        display: flex;
        justify-content: center;
        margin-bottom: 30px;

        &-img {
          width: 270px;
        }
      }

      &-button {
        display: flex;
        justify-content: center;
        padding-top: 10px;
      }
    }
  }

  .input-wrapper {
    position: relative;
    width: fit-content;
  }

  .input-group {
    display: flex;
    flex-direction: column;
    margin-bottom: 30px;
    
    label {
      color: #A6A6A6;
      font-family: $font-secondary;
      font-size: 14px;
      margin-bottom: 8px;

      &.error {
        color: #E64545;
      }
    }
    
    input {
      padding: 15px 31px 15px 22px;
      border-radius: 8px;
      border: 1px solid #535353;
      background: rgba(217, 217, 217, 0.05);
      width: 320px; 
      color: #A6A6A6;
      font-family: $font-secondary;
      outline: none;
      font-size: 16px;

      &.error {
        border-color: #E64545;
      }
    }

    input:-webkit-autofill {
      appearance: menulist-button;
      background-color: rgba(217, 217, 217, 0.05) !important;
      background: rgba(217, 217, 217, 0.05) !important;
      color: #A6A6A6 !important;
    }

    .toggle-password {
      background: none;
      border: none;
      position: absolute;
      top: 50%;
      right: 15px;
      transform: translateY(-50%);
      cursor: pointer;
      display: flex;
      align-items: center;
      padding: 0;
      color: #535353;
      width: 16px;
    }
  }

  .error-message {
    color: red;
    font-size: 12px;
    margin-top: 5px;
  }

  .button {
    font-size: 16px;
    margin-top: 25px;
    margin-bottom: 30px;
    padding: 8px 80px;
    border-radius: 8px;
    cursor: pointer;
    font-family: $font-secondary;
    background-color: #BBF90A;
    color: #232323;
    width: fit-content;
    outline: unset;

    &.outline{
      border: 1px solid rgba(187, 249, 10, 0.50);
      background-color: transparent;
      color: #BBF90A;
    }

    &:hover {
      background-color: #90C300;
      color: #232323;
    }
  }

</style>